import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowRight, Phone, BookOpen, Search, Pill, Rocket } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { trackForecastCtaClicked } from '../utils/analytics';

export const StartHere = () => {
  const navigate = useNavigate();

  const paths = [
    {
      id: 'researching',
      icon: Search,
      title: 'Just Researching',
      description: "I'm trying to understand how this all works before making any decisions.",
      cta: 'Start with:',
      link: '/blog/how-glp1-medications-work',
      linkText: 'How GLP-1 Medications Actually Work',
      color: '#3B82F6'
    },
    {
      id: 'already-on',
      icon: Pill,
      title: 'Already on Medication',
      description: "I'm currently on a GLP-1 and want to troubleshoot or get better results.",
      cta: 'Most relevant for you:',
      link: '/blog/glp1-side-effects-how-to-fix',
      linkText: 'The Most Common GLP-1 Side Effects and How to Fix Them',
      color: '#F59E0B'
    },
    {
      id: 'ready',
      icon: Rocket,
      title: 'Ready to Move Forward',
      description: "I've done enough research and want to see what my personal results could look like.",
      cta: 'Skip ahead:',
      link: '/calculator',
      linkText: 'Build Your 90-Day Forecast',
      color: '#22C55E'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0B1020]">
      <SEOHead 
        title="Start Here - Find Your Path"
        description="Not sure where to begin with GLP-1 medications? Pick your path: researching, already on medication, or ready to move forward."
        canonicalPath="/start-here"
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0B1020]/95 backdrop-blur-sm border-b border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-xl font-bold text-[#F8FAFC]">Bryan Maldonado</Link>
            <div className="flex items-center gap-4">
              <a href="tel:801-651-8363" className="hidden sm:flex items-center gap-2 text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
                <Phone size={18} />
                <span>801-651-8363</span>
              </a>
              <Button onClick={() => {
                trackForecastCtaClicked('start_here_header');
                navigate('/calculator');
              }} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">
                90-Day Forecast
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#F8FAFC] mb-4">
              Not sure where to begin? Start here.
            </h1>
            <p className="text-xl text-[#94A3B8] max-w-2xl mx-auto">
              Most people reading this have already been contacted multiple times or taken several quizzes. This is different — just pick where you are and we'll point you to what's actually useful.
            </p>
          </div>

          {/* Path Selector */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {paths.map((path) => {
              const Icon = path.icon;
              return (
                <Card
                  key={path.id}
                  className="bg-[#121A2B] border-[#1E293B] p-6 hover:border-opacity-100 transition-all duration-300 flex flex-col"
                  style={{ borderColor: `${path.color}30` }}
                  data-testid={`path-${path.id}`}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${path.color}20` }}
                  >
                    <Icon size={24} style={{ color: path.color }} />
                  </div>
                  <h2 className="text-xl font-bold text-[#F8FAFC] mb-2">{path.title}</h2>
                  <p className="text-[#94A3B8] text-sm mb-6 flex-1">{path.description}</p>
                  <div className="mt-auto">
                    <p className="text-xs text-[#64748B] mb-2">{path.cta}</p>
                    <Link
                      to={path.link}
                      className="flex items-center gap-2 font-medium hover:gap-3 transition-all"
                      style={{ color: path.color }}
                    >
                      {path.linkText}
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Quick Links */}
          <Card className="bg-[#121A2B] border-[#1E293B] p-6 mb-12">
            <h3 className="text-lg font-bold text-[#F8FAFC] mb-4 flex items-center gap-2">
              <BookOpen size={20} className="text-[#3B82F6]" />
              Popular Starting Points
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/blog/what-is-compounded-semaglutide"
                className="p-4 bg-[#0B1020] border border-[#1E293B] rounded-lg hover:border-[#3B82F6] transition-all duration-300 flex items-center justify-between"
              >
                <span className="text-[#F8FAFC] text-sm">What Is Compounded Semaglutide?</span>
                <ArrowRight size={16} className="text-[#3B82F6]" />
              </Link>
              <Link
                to="/blog/what-is-tirzepatide"
                className="p-4 bg-[#0B1020] border border-[#1E293B] rounded-lg hover:border-[#3B82F6] transition-all duration-300 flex items-center justify-between"
              >
                <span className="text-[#F8FAFC] text-sm">What Is Tirzepatide?</span>
                <ArrowRight size={16} className="text-[#3B82F6]" />
              </Link>
              <Link
                to="/blog/compounded-vs-brand-name-glp1"
                className="p-4 bg-[#0B1020] border border-[#1E293B] rounded-lg hover:border-[#3B82F6] transition-all duration-300 flex items-center justify-between"
              >
                <span className="text-[#F8FAFC] text-sm">Compounded vs Brand Name</span>
                <ArrowRight size={16} className="text-[#3B82F6]" />
              </Link>
              <Link
                to="/blog/first-30-days-glp1-program"
                className="p-4 bg-[#0B1020] border border-[#1E293B] rounded-lg hover:border-[#3B82F6] transition-all duration-300 flex items-center justify-between"
              >
                <span className="text-[#F8FAFC] text-sm">Your First 30 Days</span>
                <ArrowRight size={16} className="text-[#3B82F6]" />
              </Link>
            </div>
          </Card>

          {/* Direct Contact */}
          <div className="text-center">
            <p className="text-[#94A3B8] mb-4">
              Have a specific question? Skip the reading and ask directly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => window.location.href = 'sms:8016518363'}
                variant="outline"
                className="border-[#1E293B] bg-transparent hover:bg-[#1E293B] text-[#F8FAFC]"
              >
                <Phone className="mr-2" size={18} />
                Text Bryan: 801-651-8363
              </Button>
              <Button
                onClick={() => window.open('https://calendly.com/bryanm8623/30min', '_blank')}
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
              >
                Schedule a Quick Call
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-[#1E293B]">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-[#64748B] text-sm max-w-2xl mx-auto">
            All the information on this site is educational. Nothing here constitutes medical advice. Bryan Maldonado is a licensed professional and all programs include medical oversight — reach out directly with specific questions.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default StartHere;
